/**
 * CitiesIndex — public directory of every geo-landing the platform serves.
 * Routes at `/cities`. Groups all 250+ entries by state for quick scanning
 * and lets Google find every leaf URL via a single HTML doc.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { US_STATES, US_CITIES_BY_STATE } from "../data/us_locations";
import { toSlug } from "../data/geo_slug";

function setMeta(name, content) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function CitiesIndex() {
  useEffect(() => {
    document.title = "B2B Lead Generation by City — AgenteLead Pro";
    setMeta("description", "Browse every city covered by AgenteLead Pro. Hyper-local B2B prospecting across all 50 US states + DC. AI-powered, CRM-ready, 14-day free trial.");
    setMeta("robots", "index,follow");
  }, []);

  const statesWithCities = US_STATES
    .map((s) => ({ ...s, cities: US_CITIES_BY_STATE[s.code] || [] }))
    .filter((s) => s.cities.length > 0);

  return (
    <div data-testid="cities-index-page"
      className="min-h-screen font-body antialiased"
      style={{
        background: "radial-gradient(ellipse at 50% 0%, rgba(201,162,78,0.10), transparent 55%), #0F0820",
        color: "#FFFFFF",
      }}>
      <section className="px-6 lg:px-10 pt-16 pb-10 max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-[12px] mb-6"
          data-testid="cities-back-home"
          style={{ color: "rgba(255,255,255,0.55)" }}>
          ← AgenteLead Pro
        </Link>
        <h1 className="font-display font-black tracking-tight leading-[1.04]"
          style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}>
          B2B leads, city by city.
        </h1>
        <p className="mt-4 text-[15.5px] max-w-2xl"
          style={{ color: "rgba(255,255,255,0.72)" }}>
          {statesWithCities.length} states · {statesWithCities.reduce((a, s) => a + s.cities.length, 0)} cities.
          Pick any market — your Auto Pilot agent boots up in 60 seconds.
        </p>
      </section>

      <section className="px-6 lg:px-10 max-w-6xl mx-auto pb-24 space-y-10">
        {statesWithCities.map((s) => (
          <div key={s.code} id={s.code} data-testid={`cities-state-${s.code}`}>
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4" style={{ color: "#C9A24E" }} />
              <h2 className="font-display text-[18px] font-bold tracking-tight">
                {s.name} <span style={{ color: "rgba(255,255,255,0.45)" }}>· {s.code}</span>
              </h2>
              <span className="ml-auto text-[11px] font-mono-tech uppercase tracking-[0.18em]"
                style={{ color: "rgba(255,255,255,0.4)" }}>
                {s.cities.length} cities
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {s.cities.map((entry) => {
                const [city] = entry.split(",").map((t) => t.trim());
                const slug = toSlug(entry);
                return (
                  <Link key={slug} to={`/${slug}`}
                    data-testid={`cities-link-${slug}`}
                    className="inline-flex items-center justify-between gap-1.5 px-3 py-2 rounded-md border text-[12.5px] hover:-translate-y-0.5 transition-transform"
                    style={{
                      borderColor: "rgba(201,162,78,0.22)",
                      backgroundColor: "rgba(255,255,255,0.03)",
                      color: "rgba(255,255,255,0.85)",
                    }}>
                    {city}
                    <ArrowRight className="w-3 h-3 opacity-50" />
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
